/** Печатные документы: warehouse (склад, инвентаризация, производство, ТТК, меню). ky/en/uz — с теми же ключами (проверяется типом). */
import type { Shape } from "./shape";

export const ru = {
  unit: "Ед. изм.",
  qty: "Количество",
  price: "Цена",
  avgPrice: "Средняя цена",
  sum: "Сумма",
  total: "Итого",
  warehouse: "Склад",
  location: "Место хранения",
  filter: "Отбор",
  period: "Период",
  date: "Дата",
  status: "Статус",
  comment: "Комментарий",
  category: "Категория",
  author: "Автор",
  asOf: "по состоянию на {{date}}",
  stockSheet: "Ведомость остатков",
  productCard: "Карточка движения продукта",
  byType: "Обороты по операциям",
  movements: "Движения",
  opening: "Остаток на начало",
  closing: "Остаток на конец",
  countSheet: "Инвентаризационная опись № {{n}}",
  countSheetHint: "Посчитайте фактическое наличие и впишите количество в графу «Факт» в указанной единице.",
  countAct: "Акт результатов инвентаризации № {{n}}",
  countPreliminary: "Инвентаризация ещё не проведена — итоги предварительные.",
  startedAt: "Начата",
  postedAt: "Проведена",
  mode: "Режим",
  diffSum: "Сумма разницы",
  signCounted: "Подсчитал",
  signChecked: "Проверил",
  signChair: "Председатель комиссии",
  signMember: "Член комиссии",
  signResponsible: "Материально ответственное лицо",
  signIssued: "Составил",
  signApproved: "Утвердил",
  signStorekeeper: "Кладовщик",
  signCook: "Повар",
  requestTitle: "Заявка на склад № {{n}}",
  conversion: {
    disassembly: "Акт разделки",
    assembly: "Акт сборки",
  },
  distribution: "Распределение себестоимости",
  inputs: "Вход (списано со склада)",
  outputs: "Выход (оприходовано)",
  share: "Доля, %",
  unitCost: "Себестоимость ед.",
  expiryTitle: "Сроки годности партий",
  supplier: "Поставщик",
  daysLeft: "Дней до конца срока",
  productionTask: "Задание на производство № {{n}}",
  productionDay: "План производства на {{date}}",
  productionReport: "Отчёт о производстве",
  raw: "Сырьё по техкарте",
  orders: "Выпусков",
  recipeTitle: "Технико-технологическая карта",
  dish: "Наименование",
  version: "Версия",
  output: "Выход",
  ingredients: "Состав (закладка)",
  gross: "Брутто",
  net: "Нетто",
  waste: "Отход, %",
  loss: "Потери, %",
  cost: "Стоимость",
  costTotal: "Себестоимость выхода",
  costPerPortion: "Себестоимость порции",
  salePrice: "Цена продажи",
  foodCost: "Food cost",
  technology: "Технология приготовления",
  takeaway: "только с собой",
  priceList: "Прайс-лист",
  noCategory: "Без категории",
  item: "Позиция",
  costingTitle: "Калькуляция себестоимости блюд",
  targetFoodCost: "Целевой food cost",
  margin: "Маржа",
  markup: "Наценка, %",
  recommended: "Рекомендуемая цена",
  productsTitle: "Справочник продуктов",
  type: "Тип",
  sku: "Артикул",
  purchasePrice: "Цена закупки",
  minStock: "Минимальный остаток",
  shelfLife: "Срок хранения, дн.",
  stopListTitle: "Стоп-лист",
  remaining: "Осталось порций",
  since: "С какого времени",
  countSheetShort: "Опись",
  countActShort: "Акт",
  branch: "Филиал",
};

export const en: Shape<typeof ru> = {
  unit: "Unit",
  qty: "Quantity",
  price: "Price",
  avgPrice: "Average price",
  sum: "Amount",
  total: "Total",
  warehouse: "Warehouse",
  location: "Storage location",
  filter: "Filter",
  period: "Period",
  date: "Date",
  status: "Status",
  comment: "Comment",
  category: "Category",
  author: "Author",
  asOf: "as of {{date}}",
  stockSheet: "Stock balance sheet",
  productCard: "Product movement card",
  byType: "Turnover by operation",
  movements: "Movements",
  opening: "Opening balance",
  closing: "Closing balance",
  countSheet: "Stock count sheet No. {{n}}",
  countSheetHint: "Count what is actually on hand and write the quantity in the «Actual» column in the unit shown.",
  countAct: "Stock count results No. {{n}}",
  countPreliminary: "The count is not posted yet — results are preliminary.",
  startedAt: "Started",
  postedAt: "Posted",
  mode: "Mode",
  diffSum: "Difference amount",
  signCounted: "Counted by",
  signChecked: "Checked by",
  signChair: "Committee chair",
  signMember: "Committee member",
  signResponsible: "Person in charge",
  signIssued: "Prepared by",
  signApproved: "Approved by",
  signStorekeeper: "Storekeeper",
  signCook: "Cook",
  requestTitle: "Stock request No. {{n}}",
  conversion: {
    disassembly: "Butchering record",
    assembly: "Assembly record",
  },
  distribution: "Cost distribution",
  inputs: "Input (taken from stock)",
  outputs: "Output (put into stock)",
  share: "Share, %",
  unitCost: "Unit cost",
  expiryTitle: "Batch expiry dates",
  supplier: "Supplier",
  daysLeft: "Days left",
  productionTask: "Production order No. {{n}}",
  productionDay: "Production plan for {{date}}",
  productionReport: "Production report",
  raw: "Raw materials per recipe",
  orders: "Runs",
  recipeTitle: "Technical recipe card",
  dish: "Name",
  version: "Version",
  output: "Yield",
  ingredients: "Ingredients",
  gross: "Gross",
  net: "Net",
  waste: "Waste, %",
  loss: "Loss, %",
  cost: "Cost",
  costTotal: "Cost of yield",
  costPerPortion: "Cost per portion",
  salePrice: "Selling price",
  foodCost: "Food cost",
  technology: "Preparation method",
  takeaway: "takeaway only",
  priceList: "Price list",
  noCategory: "No category",
  item: "Item",
  costingTitle: "Dish costing",
  targetFoodCost: "Target food cost",
  margin: "Margin",
  markup: "Markup, %",
  recommended: "Recommended price",
  productsTitle: "Product list",
  type: "Type",
  sku: "SKU",
  purchasePrice: "Purchase price",
  minStock: "Minimum stock",
  shelfLife: "Shelf life, days",
  stopListTitle: "Stop list",
  remaining: "Portions left",
  since: "Since",
  countSheetShort: "Count sheet",
  countActShort: "Results",
  branch: "Branch",
};

export const ky: Shape<typeof ru> = {
  unit: "Өлч. бир.",
  qty: "Саны",
  price: "Баасы",
  avgPrice: "Орточо баа",
  sum: "Сумма",
  total: "Жыйынтык",
  warehouse: "Кампа",
  location: "Сактоо орду",
  filter: "Тандоо",
  period: "Мезгил",
  date: "Күнү",
  status: "Абалы",
  comment: "Комментарий",
  category: "Категория",
  author: "Автору",
  asOf: "{{date}} абалына",
  stockSheet: "Калдыктардын ведомосту",
  productCard: "Азыктын кыймыл карточкасы",
  byType: "Операциялар боюнча жүгүртүү",
  movements: "Кыймылдар",
  opening: "Башындагы калдык",
  closing: "Аягындагы калдык",
  countSheet: "Инвентаризациялык тизме № {{n}}",
  countSheetHint: "Иш жүзүндөгү барды санап, санын «Факт» тилкесине көрсөтүлгөн бирдикте жазыңыз.",
  countAct: "Инвентаризациянын жыйынтыгы боюнча акт № {{n}}",
  countPreliminary: "Инвентаризация али өткөрүлө элек — жыйынтыктар алдын ала.",
  startedAt: "Башталды",
  postedAt: "Өткөрүлдү",
  mode: "Режим",
  diffSum: "Айырманын суммасы",
  signCounted: "Санаган",
  signChecked: "Текшерген",
  signChair: "Комиссиянын төрагасы",
  signMember: "Комиссиянын мүчөсү",
  signResponsible: "Материалдык жоопкер адам",
  signIssued: "Түзгөн",
  signApproved: "Бекиткен",
  signStorekeeper: "Кампачы",
  signCook: "Ашпозчу",
  requestTitle: "Кампага өтүнмө № {{n}}",
  conversion: {
    disassembly: "Бөлүү акты",
    assembly: "Чогултуу акты",
  },
  distribution: "Өздүк наркты бөлүштүрүү",
  inputs: "Кириш (кампадан чыгарылды)",
  outputs: "Чыгыш (кирешеге алынды)",
  share: "Үлүшү, %",
  unitCost: "Бирдиктин өздүк наркы",
  expiryTitle: "Партиялардын жарактуулук мөөнөтү",
  supplier: "Жеткирүүчү",
  daysLeft: "Мөөнөттүн бүтүшүнө күн",
  productionTask: "Өндүрүшкө тапшырма № {{n}}",
  productionDay: "{{date}} күнгө өндүрүш планы",
  productionReport: "Өндүрүш боюнча отчёт",
  raw: "Техкарта боюнча чийки зат",
  orders: "Чыгаруулар",
  recipeTitle: "Техникалык-технологиялык карта",
  dish: "Аталышы",
  version: "Версия",
  output: "Чыгышы",
  ingredients: "Курамы (салынышы)",
  gross: "Брутто",
  net: "Нетто",
  waste: "Калдык, %",
  loss: "Жоготуу, %",
  cost: "Наркы",
  costTotal: "Чыгыштын өздүк наркы",
  costPerPortion: "Порциянын өздүк наркы",
  salePrice: "Сатуу баасы",
  foodCost: "Food cost",
  technology: "Даярдоо технологиясы",
  takeaway: "алып кетүү үчүн гана",
  priceList: "Баа тизмеси",
  noCategory: "Категориясыз",
  item: "Позиция",
  costingTitle: "Тамактардын өздүк наркын эсептөө",
  targetFoodCost: "Максаттуу food cost",
  margin: "Маржа",
  markup: "Үстөк баа, %",
  recommended: "Сунушталган баа",
  productsTitle: "Азыктардын маалымдамасы",
  type: "Түрү",
  sku: "Артикул",
  purchasePrice: "Сатып алуу баасы",
  minStock: "Минималдуу калдык",
  shelfLife: "Сактоо мөөнөтү, күн",
  stopListTitle: "Стоп-тизме",
  remaining: "Калган порциялар",
  since: "Качантан бери",
  countSheetShort: "Тизме",
  countActShort: "Акт",
  branch: "Филиал",
};

export const uz: Shape<typeof ru> = {
  unit: "O‘lch. bir.",
  qty: "Miqdor",
  price: "Narx",
  avgPrice: "O‘rtacha narx",
  sum: "Summa",
  total: "Jami",
  warehouse: "Ombor",
  location: "Saqlash joyi",
  filter: "Saralash",
  period: "Davr",
  date: "Sana",
  status: "Holat",
  comment: "Izoh",
  category: "Kategoriya",
  author: "Muallif",
  asOf: "{{date}} holatiga",
  stockSheet: "Qoldiqlar vedomosti",
  productCard: "Mahsulot harakati kartochkasi",
  byType: "Operatsiyalar bo‘yicha aylanma",
  movements: "Harakatlar",
  opening: "Boshlang‘ich qoldiq",
  closing: "Yakuniy qoldiq",
  countSheet: "Inventarizatsiya ro‘yxati № {{n}}",
  countSheetHint: "Haqiqiy mavjudlikni sanab, miqdorni «Fakt» ustuniga ko‘rsatilgan birlikda yozing.",
  countAct: "Inventarizatsiya natijalari dalolatnomasi № {{n}}",
  countPreliminary: "Inventarizatsiya hali o‘tkazilmagan — natijalar dastlabki.",
  startedAt: "Boshlangan",
  postedAt: "O‘tkazilgan",
  mode: "Rejim",
  diffSum: "Farq summasi",
  signCounted: "Sanagan",
  signChecked: "Tekshirgan",
  signChair: "Komissiya raisi",
  signMember: "Komissiya a’zosi",
  signResponsible: "Moddiy javobgar shaxs",
  signIssued: "Tuzgan",
  signApproved: "Tasdiqlagan",
  signStorekeeper: "Omborchi",
  signCook: "Oshpaz",
  requestTitle: "Omborga buyurtma № {{n}}",
  conversion: {
    disassembly: "Bo‘laklash dalolatnomasi",
    assembly: "Yig‘ish dalolatnomasi",
  },
  distribution: "Tannarxni taqsimlash",
  inputs: "Kirish (ombordan chiqarildi)",
  outputs: "Chiqish (kirim qilindi)",
  share: "Ulush, %",
  unitCost: "Birlik tannarxi",
  expiryTitle: "Partiyalarning yaroqlilik muddati",
  supplier: "Yetkazib beruvchi",
  daysLeft: "Muddat tugashigacha kun",
  productionTask: "Ishlab chiqarish topshirig‘i № {{n}}",
  productionDay: "{{date}} uchun ishlab chiqarish rejasi",
  productionReport: "Ishlab chiqarish hisoboti",
  raw: "Texkarta bo‘yicha xomashyo",
  orders: "Chiqarishlar",
  recipeTitle: "Texnik-texnologik karta",
  dish: "Nomi",
  version: "Versiya",
  output: "Chiqish",
  ingredients: "Tarkibi (solinishi)",
  gross: "Brutto",
  net: "Netto",
  waste: "Chiqindi, %",
  loss: "Yo‘qotish, %",
  cost: "Qiymat",
  costTotal: "Chiqish tannarxi",
  costPerPortion: "Porsiya tannarxi",
  salePrice: "Sotish narxi",
  foodCost: "Food cost",
  technology: "Tayyorlash texnologiyasi",
  takeaway: "faqat olib ketish uchun",
  priceList: "Narxlar ro‘yxati",
  noCategory: "Kategoriyasiz",
  item: "Pozitsiya",
  costingTitle: "Taomlar tannarxini hisoblash",
  targetFoodCost: "Maqsadli food cost",
  margin: "Marja",
  markup: "Ustama, %",
  recommended: "Tavsiya etilgan narx",
  productsTitle: "Mahsulotlar ma’lumotnomasi",
  type: "Turi",
  sku: "Artikul",
  purchasePrice: "Xarid narxi",
  minStock: "Minimal qoldiq",
  shelfLife: "Saqlash muddati, kun",
  stopListTitle: "Stop-ro‘yxat",
  remaining: "Qolgan porsiyalar",
  since: "Qachondan",
  countSheetShort: "Ro‘yxat",
  countActShort: "Dalolatnoma",
  branch: "Filial",
};
